import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';

const HOST_API = process.env.REACT_APP_URL;

const firstOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

const fmtImporte = (v) => v != null ? `$ ${Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : '—';

const FORM_EMPTY = {
    estadoFinancieroConceptoId: null,
    descripcion:                '',
    importe:                     null,
    observaciones:                '',
};

const EstadoFinancieroValoresMensuales = () => {
    const toast = useRef(null);
    const { barrio } = useContext(BarrioContext);

    const administracionId = parseInt(localStorage.getItem('administracionId') || '1');

    // ─── Data ────────────────────────────────────────────────────────────────────
    const [periodo,  setPeriodo]  = useState(firstOfMonth(new Date()));
    const [valores,  setValores]  = useState([]);
    const [loading,  setLoading]  = useState(false);

    // ─── Modal alta / edición de valor ─────────────────────────────────────────────
    const [modalVisible, setModalVisible] = useState(false);
    const [eligiendoConcepto, setEligiendoConcepto] = useState(false);
    const [form,         setForm]         = useState({ ...FORM_EMPTY });
    const [submitting,   setSubmitting]   = useState(false);

    const conceptosManuales = valores.filter(v => v.editable);

    // ─── Carga valores ───────────────────────────────────────────────────────────
    const fetchValores = useCallback(async () => {
        if (!barrio?.id || !periodo) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`${HOST_API}api/estadoFinancieroValores/GetValores`, {
                params: { barrioId: barrio.id, administracionId, periodo: firstOfMonth(periodo).toISOString() },
            });
            setValores(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los valores', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, administracionId, periodo]);

    useEffect(() => { fetchValores(); }, [fetchValores]);

    // ─── Alta / edición de valor ───────────────────────────────────────────────────
    const abrirNuevo = () => {
        setForm({ ...FORM_EMPTY });
        setEligiendoConcepto(true);
        setModalVisible(true);
    };

    const editarValor = (row) => {
        setForm({
            estadoFinancieroConceptoId: row.estadoFinancieroConceptoId,
            descripcion:                row.descripcion,
            importe:                    row.importe,
            observaciones:              row.observaciones || '',
        });
        setEligiendoConcepto(false);
        setModalVisible(true);
    };

    const seleccionarConcepto = (estadoFinancieroConceptoId) => {
        const c = conceptosManuales.find(v => v.estadoFinancieroConceptoId === estadoFinancieroConceptoId);
        setForm({
            estadoFinancieroConceptoId,
            descripcion:   c?.descripcion || '',
            importe:       c?.importe || null,
            observaciones: c?.observaciones || '',
        });
    };

    const guardarValor = async () => {
        if (!form.estadoFinancieroConceptoId) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione el concepto', life: 3000 }); return; }
        if (form.importe === null || form.importe === undefined) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el importe', life: 3000 }); return; }

        setSubmitting(true);
        try {
            const payload = {
                estadoFinancieroConceptoId: form.estadoFinancieroConceptoId,
                periodo:                    firstOfMonth(periodo).toISOString(),
                importe:                    form.importe,
                observaciones:               form.observaciones || null,
            };
            await axios.post(`${HOST_API}api/estadoFinancieroValores/SaveValor`, payload);
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Valor guardado correctamente', life: 3000 });
            setModalVisible(false);
            fetchValores();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'No se pudo guardar el valor', life: 4000 });
        } finally {
            setSubmitting(false);
        }
    };

    // ─── Columnas de la grilla ────────────────────────────────────────────────────
    const esAutomaticoBody = (row) => {
        const s = row.esAutomatico
            ? { bg: '#eff8ff', color: '#1570ef', border: '#b2ddff' }
            : { bg: '#f2f4f7', color: '#667085', border: '#d0d5dd' };
        return (
            <span style={{
                background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
            }}>
                {row.esAutomatico ? 'Automático' : 'Manual'}
            </span>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────────
    return (
        <>
            <h4>Valores Mensuales</h4>
            <br />
            <Toast ref={toast} />

            {loading && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
            )}

            <div className="flex flex-column align-items-start gap-2 mb-3">
                <Button label="Nuevo Valor Mensual" icon="pi pi-plus" className="p-button-sm"
                    onClick={abrirNuevo} disabled={!conceptosManuales.length} />
                <div>
                    <label style={lbl}>Período</label>
                    <Calendar value={periodo} onChange={(e) => setPeriodo(e.value)}
                        view="month" dateFormat="mm/yy" showButtonBar
                        className="p-inputtext-sm" style={{ width: 140 }} />
                </div>
            </div>

            <div className="grid">
                <div className="col-12">
                    <DataTable value={valores} paginator rows={25} rowsPerPageOptions={[15, 25, 50]}
                        showGridlines responsiveLayout="scroll"
                        emptyMessage="No hay conceptos cargados para este barrio"
                        className="p-datatable-gridlines bg-white">
                        <Column field="seccion"           header="Sección"      sortable style={{ width: '10rem' }} />
                        <Column field="descripcion"        header="Concepto"     sortable />
                        <Column header="Tipo" body={esAutomaticoBody} style={{ width: '9rem' }} />
                        <Column field="importe"            header="Importe"      body={(r) => fmtImporte(r.importe)} style={{ width: '10rem', textAlign: 'right' }} />
                        <Column field="observaciones"      header="Observaciones" body={(r) => r.observaciones || '—'} />
                        <Column header="Acciones" style={{ width: '6rem', textAlign: 'center' }} body={(row) => (
                            row.editable
                                ? <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm"
                                    tooltip="Editar" tooltipOptions={{ position: 'top' }}
                                    onClick={() => editarValor(row)} />
                                : null
                        )} />
                    </DataTable>
                </div>
            </div>

            {/* ─── Modal: Nuevo / Editar Valor ─────────────────────────────────── */}
            <Dialog header={eligiendoConcepto ? 'Nuevo Valor Mensual' : `Editar valor — ${form.descripcion}`} visible={modalVisible} style={{ width: '460px' }} modal
                onHide={() => setModalVisible(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalVisible(false)} disabled={submitting} />
                        <Button label="Guardar" icon="pi pi-check" className="p-button-sm" onClick={guardarValor} loading={submitting} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    {eligiendoConcepto && (
                        <div className="col-12">
                            <label style={lbl}>Concepto *</label>
                            <Dropdown value={form.estadoFinancieroConceptoId} options={conceptosManuales}
                                optionLabel="descripcion" optionValue="estadoFinancieroConceptoId"
                                onChange={(e) => seleccionarConcepto(e.value)}
                                placeholder="Seleccionar concepto" className="p-inputtext-sm" />
                        </div>
                    )}
                    <div className="col-12">
                        <label style={lbl}>Importe *</label>
                        <InputNumber value={form.importe} onValueChange={(e) => setForm(f => ({ ...f, importe: e.value }))}
                            mode="currency" currency="ARS" locale="es-AR" className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Observaciones</label>
                        <InputTextarea value={form.observaciones} onChange={(e) => setForm(f => ({ ...f, observaciones: e.target.value }))}
                            rows={3} placeholder="Observaciones opcionales" className="p-inputtext-sm" />
                    </div>
                </div>
            </Dialog>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default EstadoFinancieroValoresMensuales;
